import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Certificate, ShareNetwork, BookmarkSimple,
  Eye, Books, MapPin, Sparkle, ImageSquare,
} from "@phosphor-icons/react";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import ShareButton from "@/components/ShareButton";

/**
 * CollectionDetail — public page for an Expert Collection.
 * Renders the curator (expert), collection description, and the ordered
 * grid of nature_stories the expert bundled together.
 */
export default function CollectionDetail() {
  const { id } = useParams();
  const [state, setState] = useState({ loading: true, data: null, errorStatus: null });

  useEffect(() => {
    let alive = true;
    setState({ loading: true, data: null, errorStatus: null });
    http.get(`/collections/${id}`)
      .then(({ data }) => { if (alive) setState({ loading: false, data, errorStatus: null }); })
      .catch((err) => {
        if (!alive) return;
        setState({ loading: false, data: null, errorStatus: err?.response?.status || 0 });
      });
    return () => { alive = false; };
  }, [id]);

  if (state.loading) {
    return (
      <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="collection-loading">
        <div className="max-w-[1100px] mx-auto animate-pulse space-y-4">
          <div className="h-8 w-1/3 bg-white/60 rounded" />
          <div className="h-14 w-3/4 bg-white/60 rounded" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-52 bg-white/60 rounded-2xl" />)}
          </div>
        </div>
      </main>
    );
  }

  const { data } = state;
  if (!data?.collection) {
    const is404 = state.errorStatus === 404;
    return (
      <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="collection-notfound">
        <div className="max-w-[720px] mx-auto text-center">
          <h1 className="font-serif-display text-3xl">
            {is404 ? "Collection not found" : "Couldn't load this collection"}
          </h1>
          <p className="mt-2 text-sm text-[#3A3830]">
            {is404
              ? "This collection may be private or has been removed."
              : "Something went wrong while loading — please try again in a moment."}
          </p>
          <Link to="/experts" className="mt-4 inline-flex text-[color:var(--np-brand,#B85F2A)] underline">Browse experts</Link>
        </div>
      </main>
    );
  }

  const { collection, items = [], expert, is_owner } = data;
  const readingText = `${collection.title}. ${collection.description || ""}\n\n${items
    .map((it) => `${it.title || it.headline || ""}. ${it.summary || ""}`).join("\n\n")}`;

  return (
    <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="collection-detail">
      <div className="max-w-[1100px] mx-auto">
        <Link
          to={expert?.slug ? `/expert/${expert.slug}` : "/experts"}
          data-testid="collection-back"
          className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-muted,#3A3830)] hover:text-[#1B4332]"
        >
          <ArrowLeft size={11} weight="bold" /> {expert ? `Back to ${expert.display_name || expert.name}` : "Back to experts"}
        </Link>

        {/* Hero */}
        <header className="mt-4 rounded-3xl bg-white border border-black/10 p-6 md:p-10 shadow-sm relative overflow-hidden">
          {collection.cover_image && (
            <div className="absolute inset-0 opacity-15" aria-hidden="true">
              <img src={collection.cover_image} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="relative">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-2">
              <Books size={12} weight="fill" /> Expert collection
              {collection.topic && <span className="ml-2 rounded-full bg-[#FBF2E1] border border-black/10 px-2 py-[1px] text-[9px] tracking-[0.22em] uppercase">{collection.topic}</span>}
              {!collection.is_public && (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-300 text-amber-800 px-2 py-[1px] text-[9px] uppercase">Private</span>
              )}
            </div>
            <h1 className="mt-2 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight" data-testid="collection-title">
              {collection.title}
            </h1>
            {collection.description && (
              <p className="mt-3 max-w-3xl font-serif-display text-base md:text-lg italic text-[#3A3830] leading-relaxed" data-testid="collection-description">
                {collection.description}
              </p>
            )}

            {/* Curator strip */}
            {expert && (
              <div className="mt-6 flex items-center gap-3 flex-wrap">
                <div className="w-10 h-10 rounded-full bg-[#F5EBDC] overflow-hidden grid place-items-center flex-shrink-0">
                  {expert.profile_photo
                    ? <img src={expert.profile_photo} alt={expert.display_name} className="w-full h-full object-cover" />
                    : <Sparkle size={16} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />}
                </div>
                <div className="min-w-0">
                  <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Curated by</div>
                  <Link to={`/expert/${expert.slug}`} className="font-serif-display text-lg hover:text-[color:var(--np-brand,#B85F2A)]">
                    {expert.display_name || expert.name}
                    {expert.verified && <Certificate size={13} weight="fill" className="inline ml-1.5 text-emerald-700" />}
                  </Link>
                  {expert.professional_title && <div className="text-xs text-[#3A3830]">{expert.professional_title}</div>}
                </div>
                <div className="ml-auto flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
                    <Eye size={12} weight="fill" /> {collection.view_count || 0} views
                  </span>
                  <ShareButton
                    title={collection.title}
                    text={collection.description || `Explore this collection curated by ${expert.display_name || expert.name}`}
                    variant="chip"
                    testid="collection-share"
                  />
                  <ListenTranslateBar text={readingText} testidPrefix="collection-tools" />
                </div>
              </div>
            )}

            {/* Owner shortcuts */}
            {is_owner && (
              <div className="mt-5 pt-5 border-t border-black/10 flex items-center gap-2 flex-wrap">
                <Link
                  to="/dashboard/expert"
                  data-testid="collection-edit-link"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
                >
                  Edit in dashboard <ArrowRight size={11} weight="bold" />
                </Link>
                <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
                  You are the curator
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Items grid */}
        <section className="mt-8" data-testid="collection-items">
          <div className="flex items-center justify-between">
            <h2 className="font-serif-display text-2xl md:text-3xl leading-tight tracking-tight">
              {items.length} {items.length === 1 ? "story" : "stories"} in this collection
            </h2>
          </div>
          {items.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-dashed border-black/20 p-10 text-center bg-white/70" data-testid="collection-empty">
              <BookmarkSimple size={20} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
              <div className="font-serif-display text-2xl">Nothing here yet.</div>
              <div className="mt-1 text-sm text-[#3A3830]">The curator hasn't added any stories yet.</div>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((it, idx) => (
                <StoryCard key={it.id} it={it} idx={idx + 1} />
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

function StoryCard({ it, idx }) {
  const cover = useMemo(() => {
    if (it.cover_image) return it.cover_image;
    const m = (it.media || []).find((x) => x.url && (x.media_type === "image" || !x.media_type));
    return m?.url;
  }, [it]);
  const loc = it.location || {};
  const locLine = [loc.city, loc.state, loc.country].filter(Boolean).join(", ");
  return (
    <Link
      to={`/stories/${it.id}`}
      data-testid={`collection-item-${it.id}`}
      className="rounded-2xl border border-black/10 bg-white overflow-hidden hover:border-[color:var(--np-brand,#B85F2A)] transition-colors group"
    >
      {cover ? (
        <div className="aspect-[16/10] overflow-hidden bg-[#F5EBDC]">
          <img src={cover} alt={it.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
        </div>
      ) : (
        <div className="aspect-[16/10] bg-[#FBF2E1] grid place-items-center">
          <ImageSquare size={28} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]/40" />
        </div>
      )}
      <div className="p-4">
        <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-1.5">
          <span>#{idx}</span>
          <span className="text-[#3A3830]">·</span>
          <span>{(it.story_type || "story").replace(/_/g, " ")}</span>
          {it.category && <><span className="text-[#3A3830]">·</span><span>{it.category}</span></>}
        </div>
        <h3 className="mt-1 font-serif-display text-lg leading-tight line-clamp-2">{it.title || it.headline || "Untitled"}</h3>
        {(it.summary || it.body) && <p className="mt-1 text-xs text-[#3A3830] line-clamp-3">{it.summary || it.body}</p>}
        {locLine && (
          <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-[#3A3830]">
            <MapPin size={10} weight="fill" /> {locLine}
          </div>
        )}
      </div>
    </Link>
  );
}
